/**
 * Saved map views ("Combine layers").
 *
 * A view is just a set of layer ids shown together, in place of the rail's
 * one-at-a-time selection. Everything here is pure so it can be tested
 * without a DOM; `node --test` loads it directly, hence the explicit `.js`
 * on the relative import below.
 */
/**
 * The layer ids, spelled out rather than imported from `mapUrlParams`.
 *
 * That module reaches a JSX component through its imports, so pulling it in
 * here would stop this file loading under `node --test` and take the whole
 * model out of test. `mapViews.test.js` reads mapUrlParams as text and asserts
 * these four strings still match LAYER_MODES, so the duplication cannot drift
 * silently.
 */
const GROWING = 'growing';
const SHIPMENTS = 'shipments';
const WEATHER = 'weather';
const ALL = 'all';

/**
 * Naming order, fixed by the design: Growing, Shipments, Weather.
 *
 * Deliberately NOT the order the user ticked the boxes in — two people who
 * pick the same pair should land on the same name, and a view's name should
 * not change if it is ever rebuilt from a different direction.
 */
export const VIEW_LAYER_ORDER = [GROWING, SHIPMENTS, WEATHER];

const LABELS = {
  [GROWING]: 'Growing',
  [SHIPMENTS]: 'Shipments',
  [WEATHER]: 'Weather',
};

/** The combinable layers, in display order, ignoring anything unknown. */
export const orderLayers = (layers = []) =>
  VIEW_LAYER_ORDER.filter((id) => (layers || []).includes(id));

/** "Growing + Shipments". Empty for an empty set, which is not a valid view. */
export const viewName = (layers = []) =>
  orderLayers(layers).map((id) => LABELS[id]).join(' + ');

/**
 * Stable id derived from the layer set.
 *
 * Content-addressed on purpose: saving the same combination twice updates the
 * existing view rather than adding a duplicate the user then has to tidy up.
 */
export const viewId = (layers = []) => `view:${orderLayers(layers).join('+')}`;

/**
 * A view must combine at least two layers, and not every layer.
 *
 * Both bounds exist because a view outside them duplicates something the rail
 * already has: one layer is just that layer's tab, and all three is the All
 * tab. Expressed against `VIEW_LAYER_ORDER.length` rather than a literal 3, so
 * adding a fourth combinable layer widens the range on its own.
 */
export const MIN_VIEW_LAYERS = 2;
export const isValidView = (layers = []) => {
  const n = orderLayers(layers).length;
  return n >= MIN_VIEW_LAYERS && n < VIEW_LAYER_ORDER.length;
};

/**
 * Add (or replace) a view, newest last so the rail's order is the order they
 * were created. Replacing keeps the original position rather than jumping the
 * view to the end, which would move a tile the user is about to click.
 */
export const upsertView = (views = [], layers = []) => {
  if (!isValidView(layers)) return views || [];
  const ordered = orderLayers(layers);
  const next = { id: viewId(ordered), name: viewName(ordered), layers: ordered };
  const list = Array.isArray(views) ? views : [];
  const at = list.findIndex((v) => v?.id === next.id);
  if (at === -1) return [...list, next];
  const copy = [...list];
  copy[at] = next;
  return copy;
};

/**
 * Every layer set a view is allowed to be: at least two layers, never all of
 * them. With three combinable layers that is the three pairs.
 *
 * Enumerated rather than hardcoded so it stays correct if a fourth layer is
 * added — the rule is expressed once, in `isValidView`.
 */
export const allValidCombinations = () => {
  const out = [];
  const n = VIEW_LAYER_ORDER.length;
  for (let mask = 1; mask < (1 << n); mask += 1) {
    const layers = VIEW_LAYER_ORDER.filter((_, i) => mask & (1 << i));
    if (isValidView(layers)) out.push(layers);
  }
  return out;
};

/**
 * True when every allowed combination is already saved, so Combine has
 * nothing left to offer. The rail disables the button rather than opening a
 * popover whose Save can never enable.
 */
export const allCombinationsSaved = (views = []) => {
  const have = new Set((Array.isArray(views) ? views : []).map((v) => v?.id));
  return allValidCombinations().every((layers) => have.has(viewId(layers)));
};

export const removeView = (views = [], id) =>
  (Array.isArray(views) ? views : []).filter((v) => v?.id !== id);

/**
 * Rehydrate whatever Firestore returns.
 *
 * Stored state is user-writable in practice (it round-trips through a
 * document), so a malformed entry must not take the rail down with it: rows
 * without usable layers are dropped rather than rendered as a blank tile.
 */
export const normalizeViews = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const v of raw) {
    const layers = orderLayers(Array.isArray(v?.layers) ? v.layers : []);
    // The same rule the popover enforces. A stored row outside it — from an
    // older shape, or a hand-edited document — is dropped rather than shown
    // as a view the UI would refuse to recreate.
    if (!isValidView(layers)) continue;
    const id = viewId(layers);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: viewName(layers), layers });
  }
  return out;
};

/**
 * Which layers a given selection shows.
 *
 * The built-in modes keep their existing behaviour — notably ALL showing
 * everything and WEATHER keeping region pins, since the forecast is about the
 * places that grow the crop. A saved view shows exactly what was ticked and
 * nothing else, which is the whole point of combining.
 */
export const layersForMode = (layerMode) => {
  switch (layerMode) {
    case ALL:       return [GROWING, SHIPMENTS, WEATHER];
    case SHIPMENTS: return [SHIPMENTS];
    case WEATHER:   return [WEATHER, GROWING];
    case GROWING:   return [GROWING];
    default:        return [];
  }
};

export default {
  VIEW_LAYER_ORDER, MIN_VIEW_LAYERS, orderLayers, viewName, viewId, isValidView,
  upsertView, removeView, normalizeViews, layersForMode,
  allValidCombinations, allCombinationsSaved,
};
